import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AbstractComponent } from '../../../commons/abstract.component';
import { Channel } from '../../../models/channel.model';
import { SettingsWebhook, SettingsWebhookItem } from '../../../models/settings-webhook.model';
import { SettingsWebhookService } from '../../../services/settings-webhook.service';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { SettingsWebhookFormComponent } from './settings-webhook-form/settings-webhook-form.component';

@Component({
    selector: 'settings-webhook-component',
    // templateUrl: './settings-webhook.component.html',
    templateUrl: './settings-webhook.v2.component.html',
    styleUrls: ['settings-webhook.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettingsWebhookComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input() channel!: Channel;

  settingsWebhook: SettingsWebhook = <SettingsWebhook>{};
  filteredItems: SettingsWebhookItem[] = [];
  private searchTerms = new Subject<string>();

  constructor(
    injector: Injector,
    private settingsWebhookService: SettingsWebhookService,
    public dialog: MatDialog,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.createForm();
    this.settingsWebhook = <SettingsWebhook>{};
    this.setupSearchObservable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['channel']?.currentValue) {
      this.settingsWebhook = <SettingsWebhook>{};
      this.channel = changes['channel']?.currentValue;
      this.getSettingsModule();
    }
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      url: [false],
      event: [false],
    });
    this.formGroup.valueChanges.subscribe(value => {
      Object.assign(this.settingsWebhook, value);
    });
  }

  getSettingsModule() {
    this.settingsWebhookService.getByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settingsWebhook: SettingsWebhook) => {
          if (settingsWebhook) {
            this.settingsWebhook = settingsWebhook;
            this.formGroup.patchValue(this.settingsWebhook);
            this.applyFilter(this.searchString);
          }
        },
      });
  }

  edit(settingsWebhookItem: SettingsWebhookItem = <SettingsWebhookItem>{}): void {

    const dialogRef = this.dialog.open(SettingsWebhookFormComponent, {
      width: '600px',
      data: {
        settingsWebhook: this.settingsWebhook,
        settingsWebhookItem: settingsWebhookItem
      },
    });

    dialogRef.afterClosed().subscribe(settingsWebhookItem => {
      if (settingsWebhookItem) {
        this.settingsWebhook.items = this.settingsWebhook.items || [];
        const index = this.settingsWebhook.items.findIndex(item => item.uuid === settingsWebhookItem.uuid);
        if (index > -1) {
          this.settingsWebhook.items[index] = settingsWebhookItem;
        } else {
          settingsWebhookItem.uuid = uuidv4();
          this.settingsWebhook.items.push(settingsWebhookItem);
        }
        this.settingsWebhookService.update(this.settingsWebhook._id, this.settingsWebhook)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (settingsWebhook: SettingsWebhook) => {
              this.settingsWebhook = settingsWebhook;
              this.alertService.success();
              this.getSettingsModule();
            }
          });
      }
    });
  }

  delete(settingsWebhookItem: SettingsWebhookItem): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        const index = this.settingsWebhook.items.findIndex(item => item.uuid === settingsWebhookItem.uuid);
        if (index > -1) {
          this.settingsWebhook.items.splice(index, 1);
        }
        this.settingsWebhookService.update(this.settingsWebhook._id, this.settingsWebhook)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (settingsWebhook: SettingsWebhook) => {
              this.settingsWebhook = settingsWebhook;
              this.alertService.success();
              this.getSettingsModule();
            }
          });
      }
    })
  }

  setupSearchObservable() {
    this.searchTerms.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchString = term;
      this.applyFilter(term);
    });
  }

  applyFilter(term: string) {
    if (!term || term.trim() === '') {
      this.filteredItems = this.settingsWebhook.items || [];
    } else {
      const lowercaseTerm = term.toLowerCase();
      this.filteredItems = (this.settingsWebhook.items || []).filter(item =>
        item.name?.toLowerCase().includes(lowercaseTerm)
      );
    }
  }

  search(term: string) {
    this.searchTerms.next(term);
  }

}
